import { create } from 'zustand';
import type {
  InboxThread,
  InboxMessage,
  InboxDraft,
  InboxFilterKey,
} from '@/components/inbox/types';

/* ---------------------------------- */
/* Templates */
/* ---------------------------------- */

export type TemplateKind = 'quick' | 'full';

export interface TemplateItem {
  id: string;
  title: string;
  body: string;
  kind: TemplateKind;
}

/* ---------------------------------- */
/* Store */
/* ---------------------------------- */

interface InboxState {
  filter: InboxFilterKey;
  search: string;

  templates: TemplateItem[];

  threads: InboxThread[];
  messages: Record<string, InboxMessage[]>;
  drafts: Record<string, InboxDraft | undefined>;

  activeThreadId: string | null;

  setFilter: (f: InboxFilterKey) => void;
  setSearch: (q: string) => void;

  setActiveThread: (id: string) => void;
  markThreadRead: (threadId: string) => void;

  addMessage: (threadId: string, message: InboxMessage) => void;

  // draft
  setDraft: (threadId: string, body: string) => void;
  clearDraft: (threadId: string) => void;
}

const toSnippet = (text: string) => {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > 60 ? `${clean.slice(0, 60)}…` : clean;
};

export const useInboxStore = create<InboxState>((set) => ({
  filter: 'all',
  search: '',

  /* ---------------- Templates ---------------- */
  templates: [
    {
      id: 'qr-1',
      kind: 'quick',
      title: 'Quick Reply',
      body:
        "Hi! Thanks for reaching out. I’m currently reviewing the details and will update you shortly.",
    },
    {
      id: 'qr-2',
      kind: 'quick',
      title: 'Need Info',
      body:
        "Thanks for contacting support. Could you share the device model/serial number and any error messages you’re seeing?",
    },
    {
      id: 'ft-1',
      kind: 'full',
      title: 'Troubleshooting Steps',
      body:
        "Hi! Thanks for the details.\n\nPlease try the following steps:\n1) Restart the device\n2) Check available disk space\n3) Send a screenshot of the error\n\nWe’ll proceed after your update.\n\nThank you!",
    },
  ],

  /* ---------------- Threads ---------------- */
  threads: [
    {
      id: 't1',
      customerName: 'Jericho Rosales',
      customerEmail: 'JerichoRosales1992@gmail.com',
      status: 'needs_reply',
      lastMessageAt: '2026-02-19T15:22:00.000Z',
      lastMessageSnippet:
        'I attached the event logs. Can you check what is causing freeze?',
      lastMessageFrom: 'customer',
      unreadCount: 1,
      deletedAt: null,
      draft: null,
    },
    {
      id: 't2',
      customerName: 'Susan Babin',
      customerEmail: 'susan@client.com',
      status: 'new',
      lastMessageAt: '2026-02-19T13:05:00.000Z',
      lastMessageSnippet: 'POS on lane 2 still throws E103 after card tap.',
      lastMessageFrom: 'customer',
      unreadCount: 2,
      deletedAt: null,
      draft: null,
    },
    {
      id: 't3',
      customerName: 'Mark Dela Cruz',
      customerEmail: 'mark@client.com',
      status: 'open',
      lastMessageAt: '2026-02-19T09:17:00.000Z',
      lastMessageSnippet:
        'Monitoring looks clean after the patch. Keeping ticket open.',
      lastMessageFrom: 'agent',
      unreadCount: 0,
      deletedAt: null,
      draft: null,
    },
    {
      id: 't4',
      customerName: 'Jane Doe',
      customerEmail: 'jane@client.com',
      status: 'closed',
      lastMessageAt: '2026-02-18T12:15:00.000Z',
      lastMessageSnippet: 'Confirmed resolved on all stores. Closing now.',
      lastMessageFrom: 'agent',
      unreadCount: 0,
      deletedAt: null,
      draft: null,
    },
    {
      id: 't5',
      customerName: 'Old Thread',
      customerEmail: 'old@client.com',
      status: 'open',
      lastMessageAt: '2026-02-15T10:08:00.000Z',
      lastMessageSnippet: 'Thanks, no further help needed.',
      lastMessageFrom: 'customer',
      unreadCount: 0,
      deletedAt: '2026-02-16T08:30:00.000Z',
      draft: null,
    },
    {
      id: 't6',
      customerName: 'Miguel Alvarez',
      customerEmail: 'miguel.alvarez@northside-retail.com',
      status: 'needs_reply',
      lastMessageAt: '2026-02-18T22:42:00.000Z',
      lastMessageSnippet: 'Diagnostics uploaded. Do we proceed with RMA?',
      lastMessageFrom: 'customer',
      unreadCount: 1,
      deletedAt: null,
      draft: null,
    },
    {
      id: 't7',
      customerName: 'Paula Bennett',
      customerEmail: 'paula.bennett@oakmart.com',
      status: 'new',
      lastMessageAt: '2026-02-18T18:06:00.000Z',
      lastMessageSnippet:
        'Scanner disconnects every 10-15 minutes since update.',
      lastMessageFrom: 'customer',
      unreadCount: 3,
      deletedAt: null,
      draft: null,
    },
    {
      id: 't8',
      customerName: 'Rico Navarro',
      customerEmail: 'rico.navarro@metrofoods.ph',
      status: 'open',
      lastMessageAt: '2026-02-18T16:27:00.000Z',
      lastMessageSnippet: 'Can we do reboot after 11 PM local time?',
      lastMessageFrom: 'customer',
      unreadCount: 0,
      deletedAt: null,
      draft: {
        threadId: 't8',
        body:
          'Draft: confirm maintenance reboot window and request before/after photos.',
        updatedAt: '2026-02-18T16:30:00.000Z',
      },
    },
    {
      id: 't9',
      customerName: 'Elaine Tan',
      customerEmail: 'elaine.tan@harborfresh.io',
      status: 'closed',
      lastMessageAt: '2026-02-17T15:18:00.000Z',
      lastMessageSnippet: 'Issue fixed after reinstall. Marking as closed.',
      lastMessageFrom: 'agent',
      unreadCount: 0,
      deletedAt: null,
      draft: null,
    },
    {
      id: 't10',
      customerName: 'Ben Kline',
      customerEmail: 'ben.kline@westgrocer.net',
      status: 'needs_reply',
      lastMessageAt: '2026-02-17T11:27:00.000Z',
      lastMessageSnippet: 'Any ETA for replacement payment terminal?',
      lastMessageFrom: 'customer',
      unreadCount: 1,
      deletedAt: null,
      draft: {
        threadId: 't10',
        body: 'Draft: confirm shipping status before sending ETA to customer.',
        updatedAt: '2026-02-17T11:33:00.000Z',
      },
    },
    {
      id: 't11',
      customerName: 'Legacy Contact',
      customerEmail: 'legacy-contact@archived.example',
      status: 'open',
      lastMessageAt: '2026-02-14T16:44:00.000Z',
      lastMessageSnippet: 'No worries, you can close this now.',
      lastMessageFrom: 'customer',
      unreadCount: 0,
      deletedAt: '2026-02-15T09:00:00.000Z',
      draft: null,
    },
    {
      id: 't12',
      customerName: 'Gemma Lewis',
      customerEmail: 'gemma.lewis@silverlinefoods.com',
      status: 'open',
      lastMessageAt: '2026-02-17T10:05:00.000Z',
      lastMessageSnippet: 'Request acknowledged by Service Desk. Ticket is now in progress.',
      lastMessageFrom: 'system',
      unreadCount: 0,
      deletedAt: null,
      draft: null,
    },
    {
      id: 't13',
      customerName: 'Oscar Lim',
      customerEmail: 'oscar.lim@easttrade.co',
      status: 'new',
      lastMessageAt: '2026-02-19T08:11:00.000Z',
      lastMessageSnippet:
        'Printer queue gets stuck when printing receipts over wifi.',
      lastMessageFrom: 'customer',
      unreadCount: 1,
      deletedAt: null,
      draft: null,
    },
    {
      id: 't14',
      customerName: 'Lily Chen',
      customerEmail: 'lily.chen@freshbasket.net',
      status: 'needs_reply',
      lastMessageAt: '2026-02-18T07:54:00.000Z',
      lastMessageSnippet: 'Can your team remote in tonight for firmware update?',
      lastMessageFrom: 'customer',
      unreadCount: 0,
      deletedAt: null,
      draft: {
        threadId: 't14',
        body:
          'Draft: confirm remote session window and ask store to keep one terminal idle.',
        updatedAt: '2026-02-18T08:10:00.000Z',
      },
    },
    {
      id: 't15',
      customerName: 'Carlos Mendoza',
      customerEmail: 'carlos.mendoza@southline.market',
      status: 'open',
      lastMessageAt: '2026-02-16T20:20:00.000Z',
      lastMessageSnippet: 'Store internet stabilized after router replacement.',
      lastMessageFrom: 'customer',
      unreadCount: 0,
      deletedAt: null,
      draft: null,
    },
  ],

  /* ---------------- Messages ---------------- */
  messages: {
    t1: [
      {
        id: 'm-t1-1',
        threadId: 't1',
        fromType: 'customer',
        fromName: 'Jericho Rosales',
        fromEmail: 'JerichoRosales1992@gmail.com',
        body:
          'My workstation freezes when I switch between browser tabs and the POS app.',
        createdAt: '2026-02-19T14:51:00.000Z',
      },
      {
        id: 'm-t1-2',
        threadId: 't1',
        fromType: 'agent',
        fromName: 'Anna Smith',
        fromEmail: 'support@actionlabs.com',
        body:
          'Please send event logs from the last freeze so we can isolate the trigger.',
        createdAt: '2026-02-19T15:06:00.000Z',
      },
      {
        id: 'm-t1-3',
        threadId: 't1',
        fromType: 'customer',
        fromName: 'Jericho Rosales',
        body:
          'I attached the event logs. Can you check what is causing freeze?',
        createdAt: '2026-02-19T15:22:00.000Z',
      },
    ],
    t2: [
      {
        id: 'm-t2-1',
        threadId: 't2',
        fromType: 'customer',
        fromName: 'Susan Babin',
        fromEmail: 'susan@client.com',
        body:
          'We started getting card declines in lane 2 after this morning startup.',
        createdAt: '2026-02-19T12:44:00.000Z',
      },
      {
        id: 'm-t2-2',
        threadId: 't2',
        fromType: 'customer',
        fromName: 'Susan Babin',
        body: 'POS on lane 2 still throws E103 after card tap.',
        createdAt: '2026-02-19T13:05:00.000Z',
      },
    ],
    t3: [
      {
        id: 'm-t3-1',
        threadId: 't3',
        fromType: 'agent',
        fromName: 'Anna Smith',
        fromEmail: 'support@actionlabs.com',
        body:
          'Patch 2.1.7 was deployed to your branch. Please monitor for one full day.',
        createdAt: '2026-02-19T08:48:00.000Z',
      },
      {
        id: 'm-t3-2',
        threadId: 't3',
        fromType: 'customer',
        fromName: 'Mark Dela Cruz',
        body: 'Understood, monitoring now.',
        createdAt: '2026-02-19T09:02:00.000Z',
      },
      {
        id: 'm-t3-3',
        threadId: 't3',
        fromType: 'agent',
        fromName: 'Anna Smith',
        fromEmail: 'support@actionlabs.com',
        body: 'Monitoring looks clean after the patch. Keeping ticket open.',
        createdAt: '2026-02-19T09:17:00.000Z',
      },
    ],
    t4: [
      {
        id: 'm-t4-1',
        threadId: 't4',
        fromType: 'customer',
        fromName: 'Jane Doe',
        fromEmail: 'jane@client.com',
        body: 'All registers are printing correctly after cable reseat.',
        createdAt: '2026-02-18T11:53:00.000Z',
      },
      {
        id: 'm-t4-2',
        threadId: 't4',
        fromType: 'agent',
        fromName: 'Anna Smith',
        fromEmail: 'support@actionlabs.com',
        body: 'Confirmed resolved on all stores. Closing now.',
        createdAt: '2026-02-18T12:15:00.000Z',
      },
    ],
    t5: [
      {
        id: 'm-t5-1',
        threadId: 't5',
        fromType: 'agent',
        fromName: 'Anna Smith',
        fromEmail: 'support@actionlabs.com',
        body: 'Following up in case any assistance is still needed.',
        createdAt: '2026-02-15T09:40:00.000Z',
      },
      {
        id: 'm-t5-2',
        threadId: 't5',
        fromType: 'customer',
        fromName: 'Old Thread',
        body: 'Thanks, no further help needed.',
        createdAt: '2026-02-15T10:08:00.000Z',
      },
    ],
    t6: [
      {
        id: 'm-t6-1',
        threadId: 't6',
        fromType: 'customer',
        fromName: 'Miguel Alvarez',
        fromEmail: 'miguel.alvarez@northside-retail.com',
        body:
          'Terminal rebooted three times overnight and sync is delayed by around 20 minutes.',
        createdAt: '2026-02-18T21:58:00.000Z',
      },
      {
        id: 'm-t6-2',
        threadId: 't6',
        fromType: 'agent',
        fromName: 'Anna Smith',
        fromEmail: 'support@actionlabs.com',
        body: 'Please export diagnostics from Settings > Support and send the ZIP.',
        createdAt: '2026-02-18T22:16:00.000Z',
      },
      {
        id: 'm-t6-3',
        threadId: 't6',
        fromType: 'customer',
        fromName: 'Miguel Alvarez',
        body: 'Diagnostics uploaded. Do we proceed with RMA?',
        createdAt: '2026-02-18T22:42:00.000Z',
      },
    ],
    t7: [
      {
        id: 'm-t7-1',
        threadId: 't7',
        fromType: 'customer',
        fromName: 'Paula Bennett',
        fromEmail: 'paula.bennett@oakmart.com',
        body:
          'Barcode scanner disconnects randomly after opening shift starts.',
        createdAt: '2026-02-18T17:30:00.000Z',
      },
      {
        id: 'm-t7-2',
        threadId: 't7',
        fromType: 'customer',
        fromName: 'Paula Bennett',
        body: 'Scanner disconnects every 10-15 minutes since update.',
        createdAt: '2026-02-18T18:06:00.000Z',
      },
    ],
    t8: [
      {
        id: 'm-t8-1',
        threadId: 't8',
        fromType: 'agent',
        fromName: 'Anna Smith',
        fromEmail: 'support@actionlabs.com',
        body:
          'We can do a guided reboot with your team. Please share your maintenance window.',
        createdAt: '2026-02-18T16:02:00.000Z',
      },
      {
        id: 'm-t8-2',
        threadId: 't8',
        fromType: 'customer',
        fromName: 'Rico Navarro',
        body: 'Can we do reboot after 11 PM local time?',
        createdAt: '2026-02-18T16:27:00.000Z',
      },
    ],
    t9: [
      {
        id: 'm-t9-1',
        threadId: 't9',
        fromType: 'customer',
        fromName: 'Elaine Tan',
        fromEmail: 'elaine.tan@harborfresh.io',
        body: 'Reinstalled driver package and the cash drawer opens correctly now.',
        createdAt: '2026-02-17T15:01:00.000Z',
      },
      {
        id: 'm-t9-2',
        threadId: 't9',
        fromType: 'agent',
        fromName: 'Anna Smith',
        fromEmail: 'support@actionlabs.com',
        body: 'Issue fixed after reinstall. Marking as closed.',
        createdAt: '2026-02-17T15:18:00.000Z',
      },
    ],
    t10: [
      {
        id: 'm-t10-1',
        threadId: 't10',
        fromType: 'agent',
        fromName: 'Anna Smith',
        fromEmail: 'support@actionlabs.com',
        body:
          'Warehouse is validating stock counts. I will share ETA after dispatch confirms.',
        createdAt: '2026-02-17T11:02:00.000Z',
      },
      {
        id: 'm-t10-2',
        threadId: 't10',
        fromType: 'customer',
        fromName: 'Ben Kline',
        fromEmail: 'ben.kline@westgrocer.net',
        body: 'Any ETA for replacement payment terminal?',
        createdAt: '2026-02-17T11:27:00.000Z',
      },
    ],
    t11: [
      {
        id: 'm-t11-1',
        threadId: 't11',
        fromType: 'agent',
        fromName: 'Anna Smith',
        fromEmail: 'support@actionlabs.com',
        body: 'Checking if you still need help with the original setup request.',
        createdAt: '2026-02-14T16:20:00.000Z',
      },
      {
        id: 'm-t11-2',
        threadId: 't11',
        fromType: 'customer',
        fromName: 'Legacy Contact',
        body: 'No worries, you can close this now.',
        createdAt: '2026-02-14T16:44:00.000Z',
      },
    ],
    t12: [
      {
        id: 'm-t12-1',
        threadId: 't12',
        fromType: 'customer',
        fromName: 'Gemma Lewis',
        fromEmail: 'gemma.lewis@silverlinefoods.com',
        body: 'Can you resend invoice INV-4432? AP could not find the email.',
        createdAt: '2026-02-17T09:57:00.000Z',
      },
      {
        id: 'm-t12-2',
        threadId: 't12',
        fromType: 'system',
        fromName: 'System',
        body: 'Request acknowledged by Service Desk. Ticket is now in progress.',
        createdAt: '2026-02-17T10:05:00.000Z',
      },
    ],
    t13: [
      {
        id: 'm-t13-1',
        threadId: 't13',
        fromType: 'customer',
        fromName: 'Oscar Lim',
        fromEmail: 'oscar.lim@easttrade.co',
        body:
          'Receipt printer queue freezes when devices are connected over wifi instead of LAN.',
        createdAt: '2026-02-19T07:58:00.000Z',
      },
      {
        id: 'm-t13-2',
        threadId: 't13',
        fromType: 'customer',
        fromName: 'Oscar Lim',
        body: 'Printer queue gets stuck when printing receipts over wifi.',
        createdAt: '2026-02-19T08:11:00.000Z',
      },
    ],
    t14: [
      {
        id: 'm-t14-1',
        threadId: 't14',
        fromType: 'customer',
        fromName: 'Lily Chen',
        fromEmail: 'lily.chen@freshbasket.net',
        body: 'Need firmware update support for four terminals tonight.',
        createdAt: '2026-02-18T07:26:00.000Z',
      },
      {
        id: 'm-t14-2',
        threadId: 't14',
        fromType: 'agent',
        fromName: 'Anna Smith',
        fromEmail: 'support@actionlabs.com',
        body: 'We can assist. Please share your preferred remote window.',
        createdAt: '2026-02-18T07:40:00.000Z',
      },
      {
        id: 'm-t14-3',
        threadId: 't14',
        fromType: 'customer',
        fromName: 'Lily Chen',
        body: 'Can your team remote in tonight for firmware update?',
        createdAt: '2026-02-18T07:54:00.000Z',
      },
    ],
    t15: [
      {
        id: 'm-t15-1',
        threadId: 't15',
        fromType: 'customer',
        fromName: 'Carlos Mendoza',
        fromEmail: 'carlos.mendoza@southline.market',
        body:
          'Internet dropped twice during checkout and caused order sync delays.',
        createdAt: '2026-02-16T19:48:00.000Z',
      },
      {
        id: 'm-t15-2',
        threadId: 't15',
        fromType: 'customer',
        fromName: 'Carlos Mendoza',
        body: 'Store internet stabilized after router replacement.',
        createdAt: '2026-02-16T20:20:00.000Z',
      },
    ],
  },

  drafts: {
    t8: {
      threadId: 't8',
      body:
        'Draft: confirm maintenance reboot window and request before/after photos.',
      updatedAt: '2026-02-18T16:30:00.000Z',
    },
    t10: {
      threadId: 't10',
      body: 'Draft: confirm shipping status before sending ETA to customer.',
      updatedAt: '2026-02-17T11:33:00.000Z',
    },
    t14: {
      threadId: 't14',
      body:
        'Draft: confirm remote session window and ask store to keep one terminal idle.',
      updatedAt: '2026-02-18T08:10:00.000Z',
    },
  },

  activeThreadId: 't1',

  /* ---------------- Actions ---------------- */

  setFilter: (f) => set({ filter: f }),
  setSearch: (q) => set({ search: q }),

  setActiveThread: (id) => set({ activeThreadId: id }),

  markThreadRead: (threadId) =>
    set((state) => ({
      threads: state.threads.map((t) =>
        t.id === threadId ? { ...t, unreadCount: 0 } : t
      ),
    })),

  addMessage: (threadId, message) =>
    set((state) => {
      const nextMessages = {
        ...state.messages,
        [threadId]: [...(state.messages[threadId] || []), message],
      };

      const nextThreads = state.threads.map((t) => {
        if (t.id !== threadId) return t;
        return {
          ...t,
          lastMessageAt: message.createdAt,
          lastMessageFrom: message.fromType,
          lastMessageSnippet: toSnippet(message.body),
          unreadCount: 0,
        };
      });

      return { messages: nextMessages, threads: nextThreads };
    }),

  setDraft: (threadId, body) =>
    set((state) => {
      const draft: InboxDraft = {
        threadId,
        body,
        updatedAt: new Date().toISOString(),
      };

      const nextDrafts = { ...state.drafts, [threadId]: draft };

      const nextThreads = state.threads.map((t) =>
        t.id === threadId ? { ...t, draft } : t
      );

      return { drafts: nextDrafts, threads: nextThreads };
    }),

  clearDraft: (threadId) =>
    set((state) => {
      const nextDrafts = { ...state.drafts };
      delete nextDrafts[threadId];

      const nextThreads = state.threads.map((t) =>
        t.id === threadId ? { ...t, draft: null } : t
      );

      return { drafts: nextDrafts, threads: nextThreads };
    }),
}));
